import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authCallbackService from '../services/authCallbackService';
import { useAuth } from '../hooks/useAuth';
import SplashScreen from './SplashScreen';
import tenantService from '../services/tenantService';
import * as deviceStore from '../db/deviceStore';
import API_BASE_URL, { HOME_ROUTE, getEffectiveTenantHost } from '../config';
import { runAuthContextDeduped } from '../utils/authContextDedupe';
import { selectApiBearerToken } from '../utils/authHeaders';
import { hasAuthTokens, storeTokens, storeUserInfo, storeTokenExpiresAt } from '../utils/tokenStorage';
import { scheduleProactiveRefresh } from '../utils/proactiveTokenRefresh';

interface AuthCallbackProps {
  oidcConfig?: {
    clientId: string;
    tenantId: string;
    authority: string;
    redirectUri: string;
    requirePkce: boolean;
  } | null;
}

interface CallbackUserInfo {
  identityProvider: string;
  userId: string;
  userDetails: string;
  email?: string;
  userRoles: string[];
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ oidcConfig }) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [configWaitCount, setConfigWaitCount] = useState<number>(0);
  const [localConfig, setLocalConfig] = useState<any>(null);
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  // Try to load config immediately from cache
  useEffect(() => {
    if (!oidcConfig && !localConfig) {
      const cachedConfig = localStorage.getItem('oidc_config_cache');
      if (cachedConfig) {
        try {
          const parsedConfig = JSON.parse(cachedConfig);
          console.log('🚀 AuthCallback: Using cached OIDC configuration:', parsedConfig.clientId);
          setLocalConfig(parsedConfig);
        } catch (error) {
          console.warn('Failed to parse cached config in AuthCallback:', error);
        }
      }
    }
  }, [oidcConfig, localConfig]);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple simultaneous token exchanges
      if (isProcessing) {
        console.log('⏳ Token exchange already in progress, skipping...');
        return;
      }
      
      setIsProcessing(true);
      
      try {
        if (!authCallbackService.isCallbackUrl()) {
          console.log('Not a callback URL, redirecting to app');
          navigate(HOME_ROUTE);
          return;
        }

        // Use either the prop config or our local cached config
        const configToUse = oidcConfig || localConfig;

        // Wait for OIDC configuration to load (with more generous timeout)
        if (!configToUse) {
          console.log('⏰ OIDC configuration not yet loaded, waiting...', configWaitCount);
          if (configWaitCount < 60) { // Wait up to 30 seconds (60 * 500ms)
            setConfigWaitCount(prev => prev + 1);
            setIsProcessing(false);
            return;
          } else {
            console.error('❌ OIDC configuration failed to load within timeout');
            setStatus('error');
            setErrorMessage('Authentication configuration is taking too long to load. Please try logging in again.');
            
            // Clean up and redirect to login
            setTimeout(() => {
              navigate('/login');
            }, 2000);
            return;
          }
        }

        // Reset wait count since config is now available
        setConfigWaitCount(0);

        // Check if we already have valid tokens to prevent duplicate token exchange
        if (hasAuthTokens()) {
          console.log('Valid tokens already exist, skipping token exchange');
          setStatus('success');
          
          // Clean up the URL
          authCallbackService.cleanupCallbackUrl();
          
          // Dispatch event to trigger auth state update - useAuth will handle navigation
          window.dispatchEvent(new CustomEvent('oidc-login-success'));
          return;
        }

        console.log('Processing OAuth callback...');
        const tokenResponse = await authCallbackService.handleCallback(configToUse);
        
        if (tokenResponse) {
          console.log('Authentication successful');

          storeTokens({
            accessToken: tokenResponse.access_token,
            idToken: tokenResponse.id_token,
            refreshToken: tokenResponse.refresh_token,
          });

          if (tokenResponse.expires_in) {
            storeTokenExpiresAt(tokenResponse.expires_in);
            scheduleProactiveRefresh(tokenResponse.expires_in);
          }
          
          // Parse the ID token to get user information BEFORE dispatching success event
          let userInfo: CallbackUserInfo | null = null;
          if (tokenResponse.id_token) {
            try {
              // Decode JWT payload (second part of the token)
              const payload = tokenResponse.id_token.split('.')[1];
              const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
              
              // Detect identity provider from authority, issuer, or token issuer claim
              let identityProvider = 'aad'; // default
              const issuer = decodedPayload.iss || '';
              const authority = configToUse?.authority || '';
              
              // Check issuer first (most reliable)
              if (issuer.includes('auth0.com')) {
                identityProvider = 'auth0';
              } else if (issuer.includes('login.microsoftonline.com') || issuer.includes('microsoft.com')) {
                identityProvider = 'aad';
              } else if (authority.includes('auth0.com')) {
                identityProvider = 'auth0';
              } else if (authority.includes('login.microsoftonline.com') || authority.includes('microsoft.com')) {
                identityProvider = 'aad';
              }
              
              console.log('Detected identity provider:', identityProvider, 'from issuer:', issuer, 'authority:', authority);
              
              userInfo = {
                identityProvider: identityProvider,
                userId: decodedPayload.oid || decodedPayload.sub,
                userDetails: decodedPayload.name || decodedPayload.preferred_username || decodedPayload.email,
                email: decodedPayload.email,
                userRoles: []
              };
              
              // Store user info BEFORE dispatching success event
              storeUserInfo(userInfo);
            } catch (error) {
              console.error('Error parsing ID token:', error);
            }
          }
          
          // Now dispatch login success event AFTER user info is stored
          console.log('📡 Dispatching oidc-login-success event');
          window.dispatchEvent(new CustomEvent('oidc-login-success'));
          
          // Add user to database with Bearer token authentication
          if (userInfo && userInfo.userId && userInfo.userDetails) {
            const ensuredUser = userInfo;
            console.log('📝 Adding OIDC user to database:', {
              userId: ensuredUser.userId,
              username: ensuredUser.userDetails,
              email: (tokenResponse.id_token ? JSON.parse(atob(tokenResponse.id_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).email : undefined)
            });
            
            try {
              const userEnsuredKey = `user_ensured_${ensuredUser.userId}`;
              const apiBearerToken = selectApiBearerToken(
                tokenResponse.access_token,
                tokenResponse.id_token
              );
              const idEmail = tokenResponse.id_token
                ? (() => {
                    try {
                      return JSON.parse(
                        atob(tokenResponse.id_token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
                      ).email as string | undefined;
                    } catch {
                      return undefined;
                    }
                  })()
                : undefined;

              if (sessionStorage.getItem(userEnsuredKey) === 'true') {
                console.log('✅ User existence already ensured in this session, skipping check');
              } else {
                await runAuthContextDeduped(`ensureUser:${ensuredUser.userId}`, async () => {
                  if (sessionStorage.getItem(userEnsuredKey) === 'true') {
                    return;
                  }
                  const checkResponse = await fetch(`${API_BASE_URL}/api/getUserWithRoles`, {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-Forwarded-Host': getEffectiveTenantHost(),
                      ...(apiBearerToken ? { Authorization: `Bearer ${apiBearerToken}` } : {}),
                    },
                  });

                  if (checkResponse.status === 404) {
                    console.log('👤 User not found in DB, creating user...');
                    const addResponse = await fetch(`${API_BASE_URL}/api/addUser`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Forwarded-Host': getEffectiveTenantHost(),
                        ...(apiBearerToken ? { Authorization: `Bearer ${apiBearerToken}` } : {}),
                      },
                      body: JSON.stringify({
                        userId: ensuredUser.userId,
                        username: ensuredUser.userDetails,
                        email: idEmail,
                      }),
                    });

                    if (addResponse.ok) {
                      const result = await addResponse.json();
                      console.log('✅ OIDC user successfully added to database:', result);
                      sessionStorage.setItem(userEnsuredKey, 'true');
                    } else {
                      const error = await addResponse.json().catch(() => ({ error: { message: 'Unknown error' } }));
                      console.warn('⚠️ Failed to add OIDC user to database:', addResponse.status, error);
                    }
                  } else if (checkResponse.ok) {
                    console.log('✅ User already exists in DB, skipping addUser');
                    sessionStorage.setItem(userEnsuredKey, 'true');
                  } else {
                    console.warn('⚠️ Unexpected response when checking user existence:', checkResponse.status);
                  }
                });
              }
            } catch (error) {
              console.error('💥 Error ensuring user exists in database:', error);
            }
          }

          // Fetch and store tenant info in local database
          try {
            console.log('📝 Fetching tenant information after successful authentication...');
            const tenantInfo = await tenantService.getCurrentTenant();
            
            if (tenantInfo) {
              // Store tenant info in local database
              const tenantInfoWithTimestamp = {
                ...tenantInfo,
                last_updated: new Date().toISOString()
              };
              
              await deviceStore.putTenantInfo(tenantInfoWithTimestamp);
              console.log('✅ Tenant information stored in local database:', tenantInfoWithTimestamp);
            } else {
              console.warn('⚠️ No tenant information received from server');
            }
          } catch (error) {
            console.error('💥 Error fetching and storing tenant information:', error);
          }
          
          setStatus('success');
          
          // Clean up the URL
          authCallbackService.cleanupCallbackUrl();
          
          // Manually refresh the auth state
          console.log('🔄 Manually refreshing authentication state...');
          const authRefreshed = await refreshAuth();
          console.log('✅ Auth refresh result:', authRefreshed);
          
          if (authRefreshed) {
            setStatus('success');
            console.log('✅ Authentication successful - useAuth will handle navigation');
          } else {
            console.error('❌ Authentication refresh failed');
            setStatus('error');
            setErrorMessage('Failed to refresh authentication state');
          }
        } else {
          setStatus('error');
          setErrorMessage('Authentication failed - no token received');
        }
      } catch (error) {
        console.error('Authentication callback error:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');
        
        // Clean up the URL even on error
        authCallbackService.cleanupCallbackUrl();
        
        // Redirect to login after showing error
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [oidcConfig, navigate, refreshAuth, isProcessing, configWaitCount, localConfig]);

  // Separate effect to handle config loading retry
  useEffect(() => {
    const configToUse = oidcConfig || localConfig;
    if (!configToUse && configWaitCount > 0 && configWaitCount < 60) {
      const timer = setTimeout(() => {
        // This will trigger the main handleCallback effect to run again
        setIsProcessing(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [configWaitCount, oidcConfig, localConfig]);

  if (status === 'processing') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
        <SplashScreen />
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>
            Completing Sign In...
          </div>
          <div style={{ fontSize: '16px', opacity: 0.8 }}>
            {!(oidcConfig || localConfig) ? 'Loading authentication configuration...' : 'Processing authentication...'}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
        <SplashScreen />
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          <div>Sign In Failed</div>
          <div style={{ fontSize: '16px', marginTop: '10px' }}>
            {errorMessage}
          </div>
          <div style={{ fontSize: '14px', marginTop: '10px' }}>
            Redirecting to login page...
          </div>
        </div>
      </div>
    );
  }

  return <SplashScreen />;
};

export default AuthCallback; 